import React from 'react';
import { graphql } from 'gatsby';
import { HelmetDatoCms } from 'gatsby-source-datocms';
import { render } from 'datocms-structured-text-to-html-string';
import Accordion from './Accordion';
import ArtworkTransferOptions from './ArtworkTransferOptions';
import BackgroundImageCta from './BackgroundImageCta';
import CtaCards from './CtaCards';
import CallbackForm from './CallbackForm';
import ContainedImage from './ContainedImage';
import ContentBlocks from './ContentBlocks';
import Content from './Content';
import CreditApplicationForm from './CreditApplicationForm';
import DisplayStands from './DisplayStands';
import ExternalVideo from './ExternalVideo';
import FeaturedIcons from './FeaturedIcons';
import FeaturedList from './FeaturedList';
import FeaturedLogos from './FeaturedLogos';
import FeaturedProductCategories from './FeaturedProductCategories';
import FileDownloads from './FileDownloads';
import FullWidthImage from './FullWidthImage';
import IconContentCards from './IconContentCards';
import IconSteps from './IconSteps';
import ImageContentBlocks from './ImageContentBlocks';
import ImageContentCards from './ImageContentCards';
import MediaContent from './MediaContent';
import InternalVideo from './InternalVideo';
import LedupConfigurator from './LedupConfigurator';
import NewsletterSignup from './NewsletterSignup';
import PriceCards from './PriceCards';
import Quote from './Quote';
import ReviewsGrid from './ReviewsGrid';
import Tabs from './Tabs';
import Table from './Table';
import TeamProfiles from './TeamProfiles';
import UpcomingEvents from './UpcomingEvents';
import { buildUrl } from '../utils';

const ModularBlocks = ({ items, isTabs, isBlogDetail }) => {
  const hasItems = items && items.length > 0;
  const accordionFaqs = [];

  if (hasItems) {
    items
      .filter(
        item =>
          item.model.apiKey === 'accordion_modular_block' &&
          item.isFaqs === true,
      )
      .forEach(({ accordionItems }) => accordionFaqs.push(...accordionItems));
  }

  return (
    <>
      {accordionFaqs.length > 0 && (
        <HelmetDatoCms
          script={[
            {
              type: 'application/ld+json',
              innerHTML: `{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                  ${accordionFaqs.map(({ heading, content }) => {
                    const handleRenderNode = ({
                      record,
                      children,
                      adapter: { renderNode },
                    }) => {
                      return renderNode(
                        'a',
                        {
                          href:
                            process.env.GATSBY_SITE_URL +
                            buildUrl(record.pageUrl.slug, record.pageUrl),
                        },
                        children || record.text,
                      );
                    };

                    return JSON.stringify({
                      '@type': 'Question',
                      name: heading,
                      acceptedAnswer: {
                        '@type': 'Answer',
                        text: render(content, {
                          renderInlineRecord(data) {
                            return handleRenderNode(data);
                          },
                          renderLinkToRecord(data) {
                            return handleRenderNode(data);
                          },
                        }),
                      },
                    });
                  })}
                ]
              }`,
            },
          ]}
        />
      )}
      {hasItems &&
        items.map(item => (
          <React.Fragment key={item.id}>
            {item.model.apiKey === 'accordion_modular_block' && (
              <Accordion
                heading={item.heading}
                items={item.accordionItems}
                isFaqs={item.isFaqs}
              />
            )}
            {item.model.apiKey === 'artwork_transfer_options_modular_block' && (
              <ArtworkTransferOptions
                heading={item.heading}
                items={item.artworkTransferOptions}
              />
            )}
            {item.model.apiKey === 'background_image_cta_modular_block' && (
              <BackgroundImageCta
                heading={item.heading}
                text={item.text}
                link={item.link}
                image={item.image}
              />
            )}
            {item.model.apiKey === 'cta_cards_modular_block' && (
              <CtaCards items={item.ctaCards} />
            )}
            {item.model.apiKey === 'callback_form_modular_block' && (
              <CallbackForm
                heading={item.heading}
                text={item.text}
                formType={item.formType}
                isBlogDetail={isBlogDetail}
              />
            )}
            {item.model.apiKey === 'contained_image_modular_block' && (
              <ContainedImage image={item.image} isBlogDetail={isBlogDetail} />
            )}
            {item.model.apiKey === 'contained_image_modular_block_narrow' && (
              <ContainedImage
                image={item.image}
                isTabs={isTabs}
                isBlogDetail={isBlogDetail}
              />
            )}
            {item.model.apiKey === 'content_blocks_modular_block' && (
              <ContentBlocks items={item.contentBlocks} />
            )}
            {item.model.apiKey === 'content_modular_block' && (
              <Content
                content={item.content}
                contain={item.contain}
                twoColumns={item.twoColumns}
                highlight={item.highlight}
                isBlogDetail={isBlogDetail}
              />
            )}
            {item.model.apiKey === 'content_modular_block_tab' && (
              <Content content={item.content} isTabs={isTabs} />
            )}
            {item.model.apiKey === 'credit_application_form_modular_block' && (
              <CreditApplicationForm
                heading={item.heading}
                full={item.fullVersion}
              />
            )}
            {item.model.apiKey === 'display_stands_modular_block' && (
              <DisplayStands
                heading={item.heading}
                items={item.displayStands}
              />
            )}
            {item.model.apiKey === 'external_video_modular_block' && (
              <ExternalVideo
                video={item.externalVideo}
                isTabs={isTabs}
                isBlogDetail={isBlogDetail}
              />
            )}
            {item.model.apiKey === 'featured_icons_modular_block' && (
              <FeaturedIcons heading={item.heading} items={item.icons} />
            )}
            {item.model.apiKey === 'featured_list_modular_block' && (
              <FeaturedList
                heading={item.heading}
                items={item.listItems}
                isTabs={isTabs}
              />
            )}
            {item.model.apiKey === 'featured_logos_modular_block' && (
              <FeaturedLogos heading={item.heading} items={item.logos} />
            )}
            {item.model.apiKey ===
              'featured_product_categories_modular_block' && (
              <FeaturedProductCategories
                heading={item.heading}
                items={item.productCategories}
              />
            )}
            {item.model.apiKey === 'file_downloads_modular_block' && (
              <FileDownloads
                heading={item.heading}
                text={item.structuredText}
                items={item.fileDownloads}
              />
            )}
            {item.model.apiKey === 'full_width_image_modular_block' && (
              <FullWidthImage image={item.image} isBlogDetail={isBlogDetail} />
            )}
            {item.model.apiKey === 'icon_content_cards_modular_block' && (
              <IconContentCards
                heading={item.heading}
                text={item.text}
                items={item.iconContentCards}
              />
            )}
            {item.model.apiKey === 'icon_steps_modular_block' && (
              <IconSteps
                heading={item.heading}
                text={item.text}
                items={item.iconSteps}
              />
            )}
            {item.model.apiKey === 'image_content_blocks_modular_block' && (
              <ImageContentBlocks
                items={item.imageContentBlocks}
                startImageRight={item.startImageRight}
              />
            )}
            {item.model.apiKey === 'image_content_cards_modular_block' && (
              <ImageContentCards
                heading={item.heading}
                items={item.imageContentCards}
              />
            )}
            {item.model.apiKey === 'image_content_modular_block_v1' && (
              <MediaContent
                media={item.images}
                heading={item.heading}
                text={item.structuredText}
                link={item.link}
                flip={item.flip}
                version={1}
                isTabs={isTabs}
              />
            )}
            {item.model.apiKey === 'image_content_modular_block_v2' && (
              <MediaContent
                media={item.images}
                heading={item.heading}
                text={item.structuredText}
                link={item.link}
                flip={item.flip}
                version={2}
              />
            )}
            {item.model.apiKey === 'image_content_modular_block_v3' && (
              <MediaContent
                media={item.images}
                heading={item.heading}
                text={item.structuredText}
                link={item.link}
                flip={item.flip}
                version={3}
              />
            )}
            {item.model.apiKey === 'internal_video_content_modular_block' && (
              <MediaContent
                media={item.internalVideo}
                autoplayVideo={item.autoplayVideo}
                heading={item.heading}
                text={item.structuredText}
                link={item.link}
                flip={item.flip}
                version={item.version || isTabs ? 1 : 2}
                isTabs={isTabs}
              />
            )}
            {item.model.apiKey === 'internal_video_modular_block' && (
              <InternalVideo
                video={item.internalVideo}
                isTabs={isTabs}
                isBlogDetail={isBlogDetail}
              />
            )}
            {item.model.apiKey === 'ledup_configurator_modular_block' && (
              <LedupConfigurator />
            )}
            {item.model.apiKey === 'newsletter_signup_modular_block' && (
              <NewsletterSignup heading={item.heading} text={item.text} />
            )}
            {item.model.apiKey === 'price_cards_modular_block' && (
              <PriceCards heading={item.heading} items={item.priceCards} />
            )}
            {item.model.apiKey === 'quote_modular_block' && (
              <Quote
                author={item.author}
                quote={item.quote}
                isBlogDetail={isBlogDetail}
              />
            )}
            {item.model.apiKey === 'reviews_modular_block' && <ReviewsGrid />}
            {item.model.apiKey === 'tabs_modular_block' && (
              <Tabs heading={item.heading} items={item.tabs} />
            )}
            {item.model.apiKey === 'table_modular_block' && (
              <Table
                heading={item.heading}
                table={item.table}
                isTabs={isTabs}
              />
            )}
            {item.model.apiKey === 'team_profiles_modular_block' && (
              <TeamProfiles heading={item.heading} items={item.teamProfiles} />
            )}
            {item.model.apiKey === 'upcoming_events_modular_block' && (
              <UpcomingEvents heading={item.heading} />
            )}
          </React.Fragment>
        ))}
    </>
  );
};

export default ModularBlocks;

export const ModularBlockFragments = graphql`
  fragment AccordionModularBlockFragment on DatoCmsAccordionModularBlock {
    id
    model {
      apiKey
    }
    heading
    accordionItems {
      heading
      content {
        value
        links {
          id: originalId
          text
          pageUrl {
            ...LinkFragment
          }
        }
      }
    }
    isFaqs
  }

  fragment ArtworkTransferOptionsModularBlockFragment on DatoCmsArtworkTransferOptionsModularBlock {
    id
    model {
      apiKey
    }
    heading
    artworkTransferOptions {
      icon {
        url
        alt
      }
      heading
      text {
        value
        links {
          id: originalId
          text
          pageUrl {
            ...LinkFragment
          }
        }
      }
      link {
        text
        pageUrl {
          ...LinkFragment
        }
      }
    }
  }

  fragment BackgroundImageCtaModularBlockFragment on DatoCmsBackgroundImageCtaModularBlock {
    id
    model {
      apiKey
    }
    heading
    text
    link {
      text
      pageUrl {
        ...LinkFragment
      }
    }
    image {
      gatsbyImageData(
        width: 1920
        height: 740
        layout: FULL_WIDTH
        imgixParams: { w: "1920", h: "740" }
      )
      alt
    }
  }

  fragment CtaCardsModularBlockFragment on DatoCmsCtaCardsModularBlock {
    id
    model {
      apiKey
    }
    ctaCards {
      heading
      text
      iconOrImage {
        ... on DatoCmsCtaCardImage {
          image {
            gatsbyImageData(height: 200, imgixParams: { h: "200" })
            alt
          }
        }
        ... on DatoCmsCtaCardIcon {
          icon {
            url
            alt
          }
        }
      }
      link {
        pageUrl {
          ...LinkFragment
        }
      }
    }
  }

  fragment CallbackFormModularBlockFragment on DatoCmsCallbackFormModularBlock {
    id
    model {
      apiKey
    }
    heading
    text
    formType
  }

  fragment ContainedImageModularBlockFragment on DatoCmsContainedImageModularBlock {
    id
    model {
      apiKey
    }
    image {
      gatsbyImageData(width: 1140, imgixParams: { w: "1140" })
      alt
    }
  }

  fragment ContainedImageModularBlockNarrowFragment on DatoCmsContainedImageModularBlockNarrow {
    id
    model {
      apiKey
    }
    image {
      gatsbyImageData(width: 940, imgixParams: { w: "940" })
      alt
    }
  }

  fragment ContentBlocksModularBlockFragment on DatoCmsContentBlocksModularBlock {
    id
    model {
      apiKey
    }
    contentBlocks {
      content {
        value
        links {
          id: originalId
          text
          pageUrl {
            ...LinkFragment
          }
        }
      }
    }
  }

  fragment ContentModularBlockFragment on DatoCmsContentModularBlock {
    id
    model {
      apiKey
    }
    content {
      value
      links {
        id: originalId
        text
        pageUrl {
          ...LinkFragment
        }
      }
    }
    twoColumns
    highlight
    contain
  }

  fragment ContentModularBlockTabFragment on DatoCmsContentModularBlockTab {
    id
    model {
      apiKey
    }
    content {
      value
      links {
        id: originalId
        text
        pageUrl {
          ...LinkFragment
        }
      }
    }
  }

  fragment CreditApplicationFormModularBlockFragment on DatoCmsCreditApplicationFormModularBlock {
    id
    model {
      apiKey
    }
    heading
    fullVersion
  }

  fragment DisplayStandsModularBlockFragment on DatoCmsDisplayStandsModularBlock {
    id
    model {
      apiKey
    }
    heading
    displayStands {
      image {
        gatsbyImageData(width: 780, imgixParams: { w: "780" })
        alt
      }
      text
    }
  }

  fragment ExternalVideoModularBlockFragment on DatoCmsExternalVideoModularBlock {
    id
    model {
      apiKey
    }
    externalVideo {
      height
      width
      provider
      providerUid
      url
    }
  }

  fragment FeaturedIconsModularBlockFragment on DatoCmsFeaturedIconsModularBlock {
    id
    model {
      apiKey
    }
    heading
    icons {
      url
      alt
      title
    }
  }

  fragment FeaturedListModularBlockFragment on DatoCmsFeaturedListModularBlock {
    id
    model {
      apiKey
    }
    heading
    listItems {
      heading
      text {
        value
        links {
          id: originalId
          text
          pageUrl {
            ...LinkFragment
          }
        }
      }
    }
  }

  fragment FeaturedLogosModularBlockFragment on DatoCmsFeaturedLogosModularBlock {
    id
    model {
      apiKey
    }
    heading
    logos {
      url
      alt
    }
  }

  fragment FeaturedProductCategoriesModularBlockFragment on DatoCmsFeaturedProductCategoriesModularBlock {
    id
    model {
      apiKey
    }
    heading
    productCategories {
      ...ProductCategoryCardFragment
    }
  }

  fragment FileDownloadsModularBlockFragment on DatoCmsFileDownloadsModularBlock {
    id
    model {
      apiKey
    }
    heading
    structuredText: text {
      value
      links {
        id: originalId
        text
        pageUrl {
          ...LinkFragment
        }
      }
    }
    fileDownloads {
      title
      url
      format
    }
  }

  fragment FullWidthImageModularBlockFragment on DatoCmsFullWidthImageModularBlock {
    id
    model {
      apiKey
    }
    image {
      gatsbyImageData(
        width: 2000
        layout: FULL_WIDTH
        imgixParams: { w: "2000" }
      )
      alt
    }
  }

  fragment IconContentCardsModularBlockFragment on DatoCmsIconContentCardsModularBlock {
    id
    model {
      apiKey
    }
    heading
    text
    iconContentCards {
      icon {
        url
        alt
      }
      heading
      text
      link {
        text
        pageUrl {
          ...LinkFragment
        }
      }
    }
  }

  fragment IconStepsModularBlockFragment on DatoCmsIconStepsModularBlock {
    id
    model {
      apiKey
    }
    heading
    text
    iconSteps {
      icon {
        url
        alt
      }
      heading
      text
    }
  }

  fragment ImageContentBlocksModularBlockFragment on DatoCmsImageContentBlocksModularBlock {
    id
    model {
      apiKey
    }
    imageContentBlocks {
      image {
        gatsbyImageData(
          width: 810
          height: 740
          imgixParams: { w: "810", h: "740" }
        )
        alt
      }
      heading
      text {
        value
        links {
          id: originalId
          text
          pageUrl {
            ...LinkFragment
          }
        }
      }
      link {
        text
        pageUrl {
          ...LinkFragment
        }
      }
      backgroundColour {
        hex
      }
    }
    startImageRight
  }

  fragment ImageContentCardsModularBlockFragment on DatoCmsImageContentCardsModularBlock {
    id
    model {
      apiKey
    }
    heading
    imageContentCards {
      heading
      image {
        gatsbyImageData(
          width: 530
          height: 305
          imgixParams: { w: "530", h: "305" }
        )
        alt
      }
      text
      link {
        text
        pageUrl {
          ...LinkFragment
        }
      }
    }
  }

  fragment ImageContentModularBlockV1Fragment on DatoCmsImageContentModularBlockV1 {
    id
    model {
      apiKey
    }
    images {
      gatsbyImageData(
        width: 710
        height: 770
        imgixParams: { w: "710", h: "770" }
      )
      alt
      isImage
    }
    heading
    structuredText: text {
      value
      links {
        id: originalId
        text
        pageUrl {
          ...LinkFragment
        }
      }
    }
    link {
      text
      pageUrl {
        ...LinkFragment
      }
    }
    flip
  }

  fragment ImageContentModularBlockV2Fragment on DatoCmsImageContentModularBlockV2 {
    id
    model {
      apiKey
    }
    images {
      gatsbyImageData(
        width: 850
        height: 780
        imgixParams: { w: "850", h: "780" }
      )
      alt
      isImage
    }
    heading
    structuredText: text {
      value
      links {
        id: originalId
        text
        pageUrl {
          ...LinkFragment
        }
      }
    }
    link {
      text
      pageUrl {
        ...LinkFragment
      }
    }
    flip
  }

  fragment ImageContentModularBlockV3Fragment on DatoCmsImageContentModularBlockV3 {
    id
    model {
      apiKey
    }
    images {
      gatsbyImageData(width: 710, imgixParams: { w: "710" })
      alt
      width
      isImage
    }
    heading
    structuredText: text {
      value
      links {
        id: originalId
        text
        pageUrl {
          ...LinkFragment
        }
      }
    }
    link {
      text
      pageUrl {
        ...LinkFragment
      }
    }
    flip
  }

  fragment InternalVideoContentModularBlockFragment on DatoCmsInternalVideoContentModularBlock {
    id
    model {
      apiKey
    }
    internalVideo {
      format
      url
      video {
        thumbnailUrl
        mp4Url
      }
    }
    autoplayVideo
    heading
    structuredText: text {
      value
      links {
        id: originalId
        text
        pageUrl {
          ...LinkFragment
        }
      }
    }
    link {
      text
      pageUrl {
        ...LinkFragment
      }
    }
    flip
    version
  }

  fragment InternalVideoModularBlockFragment on DatoCmsInternalVideoModularBlock {
    id
    model {
      apiKey
    }
    internalVideo {
      format
      url
      video {
        thumbnailUrl
        mp4Url
      }
    }
  }

  fragment LedupConfiguratorModularBlockFragment on DatoCmsLedupConfiguratorModularBlock {
    id
    model {
      apiKey
    }
  }

  fragment NewsletterSignupModularBlockFragment on DatoCmsNewsletterSignupModularBlock {
    id
    model {
      apiKey
    }
    heading
    text
  }

  fragment PriceCardsModularBlockFragment on DatoCmsPriceCardsModularBlock {
    id
    model {
      apiKey
    }
    heading
    priceCards {
      heading
      price
      text
    }
  }

  fragment QuoteModularBlockFragment on DatoCmsQuoteModularBlock {
    id
    model {
      apiKey
    }
    author
    quote
  }

  fragment ReviewsModularBlockFragment on DatoCmsReviewsModularBlock {
    id
    model {
      apiKey
    }
  }

  fragment TabsModularBlockFragment on DatoCmsTabsModularBlock {
    id
    model {
      apiKey
    }
    heading
    tabs {
      ...TabFragment
    }
  }

  fragment TableModularBlockFragment on DatoCmsTableModularBlock {
    id
    model {
      apiKey
    }
    heading
    table
  }

  fragment TeamProfilesModularBlockFragment on DatoCmsTeamProfilesModularBlock {
    id
    model {
      apiKey
    }
    heading
    teamProfiles {
      image {
        gatsbyImageData(width: 120, imgixParams: { w: "120" })
        alt
      }
      name
      role
    }
  }

  fragment UpcomingEventsModularBlockFragment on DatoCmsUpcomingEventsModularBlock {
    id
    model {
      apiKey
    }
    heading
  }
`;
